import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ChevronDown } from 'lucide-react';
import { useExport, type ExportFormat } from '../hooks/useExport';
import type { ExportQuality } from '../types';

const QUALITY_OPTIONS: { value: ExportQuality; label: string }[] = [
  { value: 'best', label: 'qualityBest' },
  { value: '1080p', label: '1080p' },
  { value: '720p', label: '720p' },
];

export function ExportButton() {
  const { t } = useTranslation();
  const { exporting, progress, phase, error, startExport } = useExport();
  const [showMenu, setShowMenu] = useState(false);
  const [quality, setQuality] = useState<ExportQuality>('1080p');

  const handleExport = (format: ExportFormat) => {
    setShowMenu(false);
    startExport(format, quality);
  };

  const getButtonLabel = () => {
    if (!exporting) return t('editor.export', 'Export');
    if (phase === 'downloading') return t('editor.downloading', 'Downloading...');
    if (phase === 'mixing') return t('editor.mixing', 'Mixing...');
    return `${progress}%`;
  };

  return (
    <div className="relative">
      <div className="flex">
        <button
          onClick={() => handleExport('mp4')}
          disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-s-[var(--radius-chip)] bg-[var(--user-page-accent)] text-[var(--user-page-bg)] text-sm font-medium hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('editor.export', 'Export')}
        >
          <Download size={14} />
          {getButtonLabel()}
        </button>
        <button
          onClick={() => setShowMenu(!showMenu)}
          disabled={exporting}
          className="px-1.5 py-1.5 rounded-e-[var(--radius-chip)] bg-[var(--user-page-accent)] text-[var(--user-page-bg)] hover:brightness-110 transition-all disabled:opacity-50 border-s border-black/20"
        >
          <ChevronDown size={14} />
        </button>
      </div>

      {showMenu && (
        <div className="absolute top-full mt-1 right-0 bg-[#1A1A1E] border border-[#48484A] rounded-lg shadow-xl overflow-hidden z-50 min-w-[160px]">
          {/* Quality selector */}
          <div className="px-3 py-2 border-b border-[#48484A]">
            <div className="text-[10px] text-[#8E8E93] uppercase tracking-wide mb-1.5">
              {t('editor.exportQuality', 'Quality')}
            </div>
            <div className="flex rounded-md overflow-hidden border border-[#48484A]">
              {QUALITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setQuality(opt.value)}
                  className={`flex-1 px-2 py-1 text-[11px] font-medium transition-colors ${
                    quality === opt.value
                      ? 'bg-[var(--user-page-accent)] text-[var(--user-page-bg)]'
                      : 'bg-[#2A2A2E] text-[#E5E5EA] hover:bg-[#3A3A3E]'
                  }`}
                >
                  {opt.value === 'best' ? t('editor.qualityBest', 'Best') : opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Format options */}
          <button
            onClick={() => handleExport('mp4')}
            className="w-full text-left px-3 py-1.5 text-xs text-[#E5E5EA] hover:bg-[#2A2A2E]"
          >
            MP4 (Video + Audio)
          </button>
          <button
            onClick={() => handleExport('mp4-video-only')}
            className="w-full text-left px-3 py-1.5 text-xs text-[#E5E5EA] hover:bg-[#2A2A2E]"
          >
            MP4 (Video Only)
          </button>
          <button
            onClick={() => handleExport('wav')}
            className="w-full text-left px-3 py-1.5 text-xs text-[#E5E5EA] hover:bg-[#2A2A2E]"
          >
            {t('editor.exportWav', 'WAV (Audio Only)')}
          </button>
        </div>
      )}

      {exporting && (
        <div className="absolute -bottom-1.5 left-0 right-0 h-1 bg-black/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--user-page-accent)] transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {error && (
        <div className="absolute top-full mt-2 left-0 right-0 text-xs text-red-400 whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
}
